import api from "@/api";
import router from "@/router";
import i18n from "@/i18n";
import { parseApiError } from "@/helpers/teamAuthorization";

const { t } = i18n.global;

const emptyUsersPagination = () => ({
  data: [],
  current_page: 1,
  from: null,
  last_page: 1,
  links: [],
  next_page_url: null,
  path: null,
  per_page: 0,
  prev_page_url: null,
  to: null,
  total: 0,
});

const normalizeUsersPagination = (data = {}) => ({
  ...emptyUsersPagination(),
  ...data,
  data: Array.isArray(data?.data) ? data.data : [],
});

export default {
  state: {
    users: [],
    user: {},
    agents: [],
    usersPerPage: emptyUsersPagination(),
    currentUsersPage: 1,
    notifications: [],
    collectionAuthorization: null,
    collectionError: null,

  },

  getters: {
    users: (state) => state.users,
    user: (state) => state.user,
    usersPerPage: (state) => state.usersPerPage,
    currentUsersPage: (state) => state.currentUsersPage,
    agents: (state) => state.agents,
    notifications: (state) => state.notifications,
    userCollectionAuthorization: (state) => state.collectionAuthorization,
    userCollectionError: (state) => state.collectionError,

  },

  mutations: {
    setUsers: (state, data) => {
      state.users = data;
    },
    setUser: (state, data) => {
      state.user = data;
    },
    setUsersPerPage: (state, data) => {
      const usersPerPage = normalizeUsersPagination(data);
      state.usersPerPage = usersPerPage;
      state.currentUsersPage = usersPerPage.current_page;
    },
    setCurrentUsersPage: (state, data) => {
      state.currentUsersPage = data;
    },
    setAgents: (state, data) => {
      state.agents = data;
    },
    setNotifications: (state, data) => {
      state.notifications = data;
    },
    setUserCollectionAuthorization: (state, data) => {
      state.collectionAuthorization = data;
    },
    setUserCollectionError: (state, data) => {
      state.collectionError = data;
    },

  },

  actions: {
    refreshScopedUsers: ({ dispatch }, { teamId = null } = {}) => {
      const query = router.currentRoute.value.query;
      return dispatch("searchUsers", {
        page: Number(query.page) || 1,
        search: query.search || "",
        active_users: query.active_users,
        teamId,
      });
    },

    addTeamMember: async ({ dispatch }, { teamId, email, roleIds, user }) => {
      try {
        const response = await api({ requiresAuth: true }).post(`/v1/teams/${teamId}/members`, {
          email,
          role_ids: roleIds,
          ...(user ? { user } : {}),
        });
        await dispatch("refreshScopedUsers", { teamId });
        dispatch("showToast", {
          type: "success",
          message: response?.data?.message || t("messages.userCreatedSuccessfully"),
        });
        return response;
      } catch (err) {
        const error = parseApiError(err);
        dispatch("showToast", {
          type: "error",
          message: error.message || t("messages.failedToCreateUser"),
        });
        throw err;
      }
    },

    updateTeamMember: async ({ dispatch }, { teamId, userId, roleIds }) => {
      try {
        const response = await api({ requiresAuth: true }).patch(
          `/v1/teams/${teamId}/members/${userId}`,
          { role_ids: roleIds }
        );
        await dispatch("refreshScopedUsers", { teamId });
        dispatch("showToast", {
          type: "success",
          message: response?.data?.message || t("messages.userUpdatedSuccessfully"),
        });
        return response;
      } catch (err) {
        const error = parseApiError(err);
        dispatch("showToast", {
          type: "error",
          message: error.message || t("messages.failedToUpdateUser"),
        });
        throw err;
      }
    },

    removeTeamMember: async ({ dispatch }, { teamId, userId }) => {
      try {
        const response = await api({ requiresAuth: true }).delete(
          `/v1/teams/${teamId}/members/${userId}`
        );
        await dispatch("refreshScopedUsers", { teamId });
        dispatch("showToast", {
          type: "success",
          message: response?.data?.message || t("messages.userDeletedSuccessfully"),
        });
        return response;
      } catch (err) {
        const error = parseApiError(err);
        const dependencyCounts = error.code === "membership_has_dependencies"
          ? Object.entries(error.dependencies || {})
              .map(([category, count]) => `${category}: ${count}`)
              .join(", ")
          : "";
        dispatch("showToast", {
          type: "error",
          message: dependencyCounts ? `${error.message} (${dependencyCounts})` : error.message,
        });
        return null;
      }
    },

    getUsers: async ({ commit }) => {
      const response = await api({ requiresAuth: true }).get("/v1/users");
      const { data } = response.data;
      commit("setUsers", data);
    },

    deleteUser: async ({ commit, dispatch, getters }, userId) => {
      commit("setUsersLoading", true);
      try {
        const response = await api({ requiresAuth: true }).delete(`/v1/users/${userId}`);
        await dispatch("getUsers");
        await dispatch("getUsersPage", getters.currentUsersPage);
        dispatch("showToast", {
          type: "success",
          message: response?.data?.message || t("messages.userDeletedSuccessfully")
        });
      } catch (err) {
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.failedToDeleteUser")
        }); 
      }
      commit("setUsersLoading", false);
    },

    disableUser: async ({ commit, dispatch, getters }, userId) => {
      commit("setUsersLoading", true);
      try {
        const response = await api({ requiresAuth: true }).patch(`/v1/users/${userId}/disable`);
        await dispatch("getUsers");
        await dispatch("getUsersPage", getters.currentUsersPage);
        dispatch("showToast", {
          type: "success",
          message: response?.data?.message || t("messages.userDisabledSuccessfully")
        });
      } catch (err) {
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.failedToDisableUser")
        }); 
      }
      commit("setUsersLoading", false);
    },

    enableUser: async ({ commit, dispatch, getters }, userId) => {
      commit("setUsersLoading", true);
      try {
        const response = await api({ requiresAuth: true }).patch(`/v1/users/${userId}/enable`);
        await dispatch("getUsers");
        await dispatch("getUsersPage", getters.currentUsersPage);
        dispatch("showToast", {
          type: "success",
          message: response?.data?.message || t("messages.userEnabledSuccessfully")
        });
      } catch (err) {
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.failedToEnableUser")
        }); 
      }
      commit("setUsersLoading", false);
    },

    createUser: (
      { commit, dispatch, getters },
      {
        firstName,
        lastName,
        email,
        password,
        titleId,
        companyName,
        gender,
        countryCode,
        birthday,
        password_confirmation,
        //prefix,
        phone,
        ahvNr,
        finmaNr,
        visibleToAll,
        roles,
        user_addresses,
        user_banks,
        settings,
        teams,
        supervisorId,
        salesCategoryId,
        salesCategoryId2,
        language,
        file,
        taxes,
        has_deposit,
        max_deposit
      }
    ) =>
      new Promise((resolve, reject) => {
        api({ requiresAuth: true })
          .post("/v1/users", {
            first_name: firstName,
            last_name: lastName,
            email,
            password,
            title_id: titleId,
            company_name: companyName,
            gender_id: gender,
            country_code: countryCode,
            birthday,
            phone,
            //   prefix,
            ahv_number: ahvNr,
            finma_nr: finmaNr,
            visible_to_all: visibleToAll,
            roles,
            user_addresses,
            user_banks,
            password_confirmation,
            settings,
            teams,
            supervisor_id: supervisorId,
            language_id: language,
            sales_category_id: salesCategoryId,
            second_sales_category: salesCategoryId2,
            taxes: taxes,
            has_deposit: has_deposit,
            max_deposit: max_deposit,

          })
          .then(
            async (response) => {
              const { data, message } = response.data;
              const { name, account } = user_banks[0];

              if (data.id && name) {
                if (account) {
                  await dispatch("addUserBank", {
                    user_id: data.id,
                    name,
                    account,
                  });
                }
              }

              if (file) {
                await dispatch("addUserPhoto", { userId: data.id, file });
              }
              commit("setUsersLoading", true);
              await dispatch("getUsersPage", getters.currentUsersPage);
              dispatch("showToast", {
                type: "success",
                message: message || t("messages.userCreatedSuccessfully")
              });
              resolve(data);
              commit("setUsersLoading", false);
            },
            (err) => {
              dispatch("showToast", {
                type: "error",
                message: err.response?.data?.message || t("messages.failedToCreateUser")
              });
              commit("setUsersLoading", false);
              reject(err);
            }
          );
      }),

    getUser: ({ dispatch }, userId) =>
      new Promise((resolve, reject) => {
        api({ requiresAuth: true })
          .get(`/v1/users/${userId}`)
          .then(
            (response) => {
              const { data } = response.data;
              resolve(data);
            },
            (error) => {
              reject(error);
              dispatch("showToast", {
                type: "error",
                message: error.response?.data?.message || t("messages.failedToGetUser")
              });
            }
          );
      }),

    getProfile: ({ dispatch, commit }) =>
      new Promise((resolve, reject) => {
        api({ requiresAuth: true })
          .get(`/v1/profile`)
          .then(
            (response) => {
              const { data, authorization } = response.data;
              commit("setLoggedUser", data);
              localStorage.setItem("user", JSON.stringify(data));
              if (authorization) {
                commit("replaceAuthorization", {
                  payload: authorization,
                  isSuperAdmin: data?.is_super_admin === true,
                });
              }
              resolve(data);
            },
            (error) => {
              reject(error);
              dispatch("showToast", {
                type: "error",
                message: error.response?.data?.message || t("messages.failedToGetUser")
              });
            }
          );
      }),

    addUserPhoto: ({dispatch}, { userId, file }) => {
      const resolvedUserId = userId && typeof userId === "object" ? userId.id : userId;

      if (!resolvedUserId) {
        dispatch("showToast", {
          type: "error",
          message: t("messages.invalidId")
        });
        return;
      }

      const formData = new FormData();
      formData.append("photo", file);
      return api({ requiresAuth: true }).post(`/v1/users/${resolvedUserId}/files`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    },

    updateUser: ({ commit, dispatch }, { user, userId }) => {
      return new Promise((resolve, reject) => {
        const {
          companyName,
          firstName,
          lastName,
          email,
          titleId,
          gender,
          password,
          confirmPassword,
          countryCode,
          birthday,
          phone,
          prefix,
          ahvNr,
          finmaNr,
          visibleToAll,
          roles,
          user_addresses,
          user_banks,
          settings,
          teams,
          supervisorId,
          salesCategoryId,
          salesCategoryId2,
          language,
          file,
          taxes,
          has_deposit,
          max_deposit,
        } = user;

        const executeUpdate = async () => {
          try {
            const response = await api({ requiresAuth: true }).patch(`/v1/users/${userId}`, {
              company_name: companyName,
              first_name: firstName,
              last_name: lastName,
              email,
              title_id: titleId,
              gender_id: gender,
              country_code: countryCode,
              birthday,
              phone,
              prefix,
              password,
              password_confirmation: confirmPassword,
              ahv_number: ahvNr,
              finma_nr: finmaNr,
              visible_to_all: visibleToAll,
              roles,
              user_addresses,
              user_banks,
              settings,
              teams,
              supervisor_id: supervisorId,
              language_id: language,
              sales_category_id: salesCategoryId,
              second_sales_category: salesCategoryId2,
              taxes,
              has_deposit,
              max_deposit,
            });

            const { data, message } = response.data;
            const firstBank = Array.isArray(user_banks) ? user_banks[0] : null;
            const bankId = firstBank?.id;
            const name = firstBank?.name?.trim?.() || "";
            const account = firstBank?.account?.trim?.() || "";

            if (!name && !account) {
              // Nothing provided: do not touch any user bank record.
            } else if (!bankId) {
              await dispatch("addUserBank", { userId: data.id, name, account });
            } else {
              await dispatch("updateUserBank", { bankId, userId: data.id, name, account });
            }

            if (file && router.currentRoute.value.name !== "AccountDetails") {
              await dispatch("addUserPhoto", { userId: data.id, file });
            }

            if (JSON.parse(localStorage.getItem("user")).id === data.id) {
              sessionStorage.setItem("needs_reset", false);
            }

            commit("setUsersLoading", true);
            const { page } = router.currentRoute.value.query;

            if (router.currentRoute.value.name === "Users") {
              await dispatch("getUsersPage", { page });
            }

            dispatch("showToast", {
              type: "success",
              message: message || t("messages.userUpdatedSuccessfully")
            });
            resolve(data);
            commit("setUsersLoading", false);
          } catch (err) {
            commit("setUsersLoading", false);

            dispatch("showToast", {
              type: "error",
              message: err.response?.data?.message || t("messages.failedToUpdateUser")
            });

            reject(err);
          }
        };

        executeUpdate();
      });
    },

    updateProfile: async ({ commit, dispatch }, { user }) => {
      commit("setUsersLoading", true);

      try {
        const {
          firstName,
          lastName,
          email,
          titleId,
          gender,
          password,
          confirmPassword,
          countryCode,
          birthday,
          phone,
          prefix,
          ahvNr,
          finmaNr,
          roles,
          user_addresses,
          user_banks,
          settings,
          language,
          file,
          signature,
        } = user;

        const formData = new FormData();

        formData.append("_method", "PATCH");

        formData.append("first_name", firstName?.trim() ?? "");
        formData.append("last_name", lastName?.trim() ?? "");
        formData.append("email", email?.trim() ?? "");
        formData.append("title_id", titleId ?? "");
        formData.append("gender_id", gender ?? "");
        formData.append("country_code", countryCode ?? "");
        formData.append("birthday", birthday ?? "");
        formData.append("phone", phone ?? "");
        formData.append("prefix", prefix ?? "");
        formData.append("ahv_number", ahvNr ?? "");
        formData.append("finma_nr", finmaNr ?? "");
        formData.append("language_id", language ?? "");

        if (password) {
          formData.append("password", password);
          formData.append("password_confirmation", confirmPassword ?? "");
        }

        if (Array.isArray(roles)) {
          roles.forEach((roleId, i) => {
            formData.append(`roles[${i}]`, roleId);
          });
        }

        if (Array.isArray(user_addresses)) {
          user_addresses.forEach((address, i) => {
            Object.entries(address).forEach(([key, value]) => {
              formData.append(
                `user_addresses[${i}][${key}]`,
                value ?? ""
              );
            });
          });
        }

        if (settings && typeof settings === "object") {
          Object.entries(settings).forEach(([key, value]) => {
            formData.append(`settings[${key}]`, value ?? "");
          });
        }

        if (signature instanceof File) {
          formData.append("signature", signature);
        }

        const response = await api({ requiresAuth: true }).post(
          "/v1/profile",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const { data, message } = response.data;

        if (file) {
          await dispatch("addUserPhoto", {
            userId: data.id,
            file,
          });
        }

        if (Array.isArray(user_banks) && user_banks.length) {
          const { id: bankId, name, account } = user_banks[0];

          if (!bankId && (name || account)) {
            await dispatch("addUserBank", {
              userId: data.id,
              name,
              account,
            });
          } else if (bankId) {
            await dispatch("updateUserBank", {
              bankId,
              userId: data.id,
              name,
              account,
            });
          }
        }

        if (router.currentRoute.value.name === "Users") {
          const { page } = router.currentRoute.value.query;
          await dispatch("getUsersPage", { page });
        }

        sessionStorage.setItem("needs_reset", false);

        dispatch("showToast", {
          type: "success",
          message: message || t("messages.profileUpdatedSuccessfully")
        });
        commit("setUsersLoading", false);
        return data;

      } catch (error) {
        dispatch("showToast", {
          type: "error",
          message: error.response?.data?.message || t("messages.failedToUpdateProfile")
        });
        commit("setUsersLoading", false);
        throw error;
      }
    },

    resetUserSignature: async (_, { userId }) => {
      await api({ requiresAuth: true }).delete(
        `/v1/users/${userId}/signature`
      );
    },

    getUsersPage: async ({ commit, dispatch }, payload = {}) => {
      const { page = 1, search } = typeof payload === "object" && payload !== null
        ? payload
        : { page: payload };

      try {
        let response = "";
        if (search) {
          response = await api({ requiresAuth: true }).get(
            `/v1/search/users?page=${page}&search=${search}`
          );
          if (
            Number(router.currentRoute?.value?.query?.page) !==
            response.data.meta.current_page
          ) {
            router.replace({
              query: {
                page: response.data.meta.current_page,
              },
            });
          }
        } else {
          response = await api({ requiresAuth: true }).get(
            `/v1/users?page=${page}`
          );
        }
        const { data } = response;
        commit("setUsersPerPage", data);
      } catch (err) {
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.failedToLoadUsers")
        }); 
      }
    },

    getNotifications: async ({ commit, dispatch }) => {
      try {
        const response = await api({ requiresAuth: true }).get(
          "/v1/user-notifications"
        );
        const { data } = response.data;
        commit("setNotifications", data);
      } catch (err) {
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.failedToLoadNotifications")
        }); 
      }
    },

    readNotification: async ({ dispatch }, notificationId) => {
      try {
        await api({ requiresAuth: true }).patch(
          `/v1/user-notifications/${notificationId}`
        );
      } catch (err) {
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.failedToReadNotification")
        }); 
      }
    },

    changeUserPassword: async ({ dispatch }, { userId, password, confirmPassword }) => {
      try {
        const response = await api({ requiresAuth: true }).patch(`/v1/users/${userId}/passwords`, {
          password,
          password_confirmation: confirmPassword,
        });

        dispatch("showToast", {
          type: "success",
          message: response?.data?.message || t("messages.passwordChangedSuccessfully")
        });

        return response;
      } catch (err) {
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.failedToChangePassword")
        });
        throw err;
      }
    },

    resetUserPassword: async ({ dispatch }, userId) => {
      try {
        const response = await api({ requiresAuth: true }).post(`/v1/users/${userId}/reset-passwords`);

        dispatch("showToast", {
          type: "success",
          message: response?.data?.message || t("messages.passwordResetSuccessfully")
        });

        return response;
      } catch (err) {
        dispatch("showToast", {
          type: "error",
          message: err.response?.data?.message || t("messages.failedToResetPassword")
        });
        throw err;
      }
    },
  },
};
